/**
 * fc-encryption/crypto/kdf.ts
 * 
 * Key derivation function. Two paths:
 * - Primary: Argon2id (via argon2-browser, lazy-loaded)
 * - Fallback: PBKDF2-SHA-256 with 600,000 iterations (OWASP 2023+)
 * 
 * Both produce a 256-bit key suitable for AES-256-GCM.
 */

import { arrayBufferToBase64, base64ToArrayBuffer } from './salts';

/** Argon2id parameters. OWASP-recommended for interactive use. */
const ARGON2_TIME = 3;
const ARGON2_MEMORY = 64 * 1024; // 64 MB in KB
const ARGON2_PARALLELISM = 1;
const ARGON2_HASH_LEN = 32; // 256 bits

/** PBKDF2 parameters. OWASP 2023+ minimum. */
const PBKDF2_ITERATIONS = 600_000;
const PBKDF2_HASH = 'SHA-256';

/** Detection: argon2-browser module is lazy-loaded only when the platform supports WASM. */
let argon2Module: any = null;

/**
 * The specifier, assembled at RUNTIME on purpose.
 *
 * F 140 — `import('argon2-browser')` as a LITERAL was correct for the import
 * transform (`@vite-ignore` stops Vite rewriting it) but useless for the other
 * half of a consumer's build: Vite's dependency pre-scan runs over
 * node_modules with esbuild, matches the bare string, and tries to PRE-BUNDLE
 * argon2-browser. That package imports `argon2.wasm`, so the scan dies unless
 * the consumer installed vite-plugin-wasm. Every Vite consumer hit this; the
 * workaround was an alias to a throwing shim, in each consumer's vite.config.
 *
 * Assembling the string means no scanner can match a bare specifier, so there
 * is nothing to pre-bundle and the build succeeds with no consumer config.
 * The runtime string is identical, so Node/ESM consumers — where argon2 loads
 * for real — are unaffected.
 *
 * This makes the PBKDF2 fallback the BUILT-IN behaviour for bundler consumers
 * rather than something each of them had to rediscover. See the caveat below.
 */
const ARGON2_SPECIFIER = ['argon2', 'browser'].join('-');

/**
 * Try to lazy-load argon2-browser. If the WASM fails (old browser, bundler, or
 * test environment), return null and the caller falls back to PBKDF2.
 *
 * CAVEAT — the KDF used to derive a key is NOT recorded next to the
 * ciphertext (only `algorithm: 'AES-256-GCM'` is). So an install that derives
 * with Argon2id and later derives with PBKDF2 gets a DIFFERENT key and cannot
 * read its own older data. That hazard predates this change — every consumer's
 * manual shim already triggered it — but this change makes the fallback
 * universal for bundler consumers, so it is worth a dispatch of its own.
 */
async function loadArgon2(): Promise<any> {
  if (argon2Module !== null) return argon2Module;
  try {
    const mod = await import(/* @vite-ignore */ ARGON2_SPECIFIER);
    argon2Module = mod;
    return mod;
  } catch (e) {
    return null;
  }
}


/**
 * F 145 D — which KDF actually produced a key.
 *
 * Until now nothing recorded it, so a record written by an install that could
 * load argon2 and a record written by one that could not were
 * indistinguishable. An install that derived with Argon2id and later derived
 * with PBKDF2 gets a DIFFERENT key and cannot read its own older data.
 */
export type KdfId = 'argon2id' | 'pbkdf2';

export interface DerivedKey {
  key: string;
  kdf: KdfId;
}

/**
 * Derive and REPORT which KDF was used.
 *
 * Every new write goes through this, so a record can record its own KDF and
 * the ambiguity stops growing. Existing callers that only need the key keep
 * using `deriveKey`.
 */
export async function deriveKeyWithId(passphrase: string, salt: string): Promise<DerivedKey> {
  const argon2 = await loadArgon2();
  if (argon2) {
    try {
      const result = await argon2.hash({
        pass: passphrase,
        salt,
        time: ARGON2_TIME,
        mem: ARGON2_MEMORY,
        parallelism: ARGON2_PARALLELISM,
        hashLen: ARGON2_HASH_LEN,
        type: argon2.ArgonType.Argon2id,
      });
      return { key: hexToBase64(result.hash), kdf: 'argon2id' };
    } catch {
      // fall through
    }
  }
  return { key: await deriveKeyPBKDF2(passphrase, salt), kdf: 'pbkdf2' };
}

/**
 * Derive with a SPECIFIC KDF, ignoring the automatic preference.
 *
 * This is what makes a legacy record readable. Given the same passphrase and
 * salt, only one of these two produces the original key; the other produces a
 * wrong one, which AES-GCM then rejects during authentication. So a caller can
 * try the recorded KDF, and on an auth failure try the other — a probe that is
 * both safe and self-verifying rather than a guess.
 */
export async function deriveKeyWithKdf(
  passphrase: string,
  salt: string,
  kdf: KdfId,
): Promise<string> {
  if (kdf === 'pbkdf2') return deriveKeyPBKDF2(passphrase, salt);

  const argon2 = await loadArgon2();
  if (!argon2) {
    throw new Error(
      '[kdf] argon2id was requested but the module is unavailable. ' +
      'This install cannot read a record written with Argon2id.'
    );
  }
  const result = await argon2.hash({
    pass: passphrase,
    salt,
    time: ARGON2_TIME,
    mem: ARGON2_MEMORY,
    parallelism: ARGON2_PARALLELISM,
    hashLen: ARGON2_HASH_LEN,
    type: argon2.ArgonType.Argon2id,
  });
  return hexToBase64(result.hash);
}

/** The KDF a record with no recorded field most likely used. */
export const LEGACY_DEFAULT_KDF: KdfId = 'argon2id';

/**
 * Derive a 256-bit AES key from a passphrase + salt.
 * 
 * @param passphrase - the user's passphrase
 * @param salt - base64-encoded salt (16 bytes)
 * @returns base64-encoded 32-byte key
 */
export async function deriveKey(passphrase: string, salt: string): Promise<string> {
  // Try Argon2id first
  const argon2 = await loadArgon2();
  if (argon2) {
    try {
      // argon2-browser API: hash({ pass, salt, time, mem, parallelism, hashLen, type })
      const result = await argon2.hash({
        pass: passphrase,
        salt: salt,
        time: ARGON2_TIME,
        mem: ARGON2_MEMORY,
        parallelism: ARGON2_PARALLELISM,
        hashLen: ARGON2_HASH_LEN,
        type: argon2.ArgonType.Argon2id,
      });
      // result.hash is hex; convert to base64
      return hexToBase64(result.hash);
    } catch (e) {
      // Fall through to PBKDF2
    }
  }

  // PBKDF2 fallback
  return deriveKeyPBKDF2(passphrase, salt);
}

/**
 * PBKDF2-SHA-256 path. Always available via Web Crypto API.
 */
async function deriveKeyPBKDF2(passphrase: string, salt: string): Promise<string> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const saltBuf = base64ToArrayBuffer(salt);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuf,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    256
  );
  return arrayBufferToBase64(bits);
}

function hexToBase64(hex: string): string {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return arrayBufferToBase64(bytes.buffer);
}
